const OUTLOOK_COPY_FONT_FACE = 'Arial';
const OUTLOOK_COPY_FONT_FAMILY = `${OUTLOOK_COPY_FONT_FACE}, sans-serif`;
const OUTLOOK_COPY_FONT_SIZE = '10pt';
const OUTLOOK_COPY_LEGACY_FONT_SIZE = '2';
const OUTLOOK_COPY_LINE_HEIGHT = 'normal';
const OUTLOOK_COPY_TEXT_COLOR = '#000000';
const OUTLOOK_COPY_LINK_COLOR = '#0957C3';
const OUTLOOK_COPY_TABLE_BORDER_COLOR = '#808080';
const OUTLOOK_COPY_LAYOUT_TABLE_BORDER_COLOR = 'transparent';
const OUTLOOK_COPY_PURPLE = '#7f35b2';
const OUTLOOK_COPY_TABLE_HEADER_BACKGROUND_COLOR = OUTLOOK_COPY_PURPLE;
const OUTLOOK_COPY_TABLE_CELL_PADDING = '4px 8px';
const OUTLOOK_COPY_GROUPED_TABLE_TITLE_BACKGROUND_COLOR = OUTLOOK_COPY_PURPLE;
const OUTLOOK_COPY_GROUPED_TABLE_TITLE_TEXT_COLOR = '#FFFFFF';
const OUTLOOK_COPY_GROUPED_TABLE_HEADER_BACKGROUND_COLOR = '#D8DBD8';
const GROUPED_TABLE_TITLE_CELL_ATTRIBUTE = 'data-nova-copy-table-title-cell';
const GROUPED_TABLE_HEADER_ROW_ATTRIBUTE = 'data-nova-copy-table-header-row';
const GROUPED_TABLE_ATTRIBUTE = 'data-nova-copy-grouped-table';

const OUTLOOK_COPY_TEXT_SELECTOR = [
  'a',
  'b',
  'blockquote',
  'div',
  'em',
  'font',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'i',
  'li',
  'ol',
  'p',
  'span',
  'strong',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
].join(',');

type ClipboardCopyEvent = {
  clipboardData?: Pick<DataTransfer, 'setData'> | null;
  preventDefault: () => void;
};

type ClipboardItemConstructor = new (items: Record<string, Blob>) => ClipboardItem;

function getTableColumnCount(rows: HTMLTableRowElement[]) {
  return Math.max(
    0,
    ...rows.map((row) => Array.from(row.cells).reduce((columnCount, cell) => columnCount + cell.colSpan, 0)),
  );
}

function isTableTitleRow(row: HTMLTableRowElement, tableColumnCount: number) {
  const [cell] = row.cells;

  return (
    row.cells.length === 1 &&
    cell?.tagName.toLowerCase() === 'th' &&
    cell.colSpan > 1 &&
    cell.colSpan >= tableColumnCount
  );
}

function isTableColumnHeaderRow(row: HTMLTableRowElement) {
  return row.cells.length > 1 && Array.from(row.cells).every((cell) => cell.tagName.toLowerCase() === 'th');
}

function prepareGroupedHeaderTables(element: HTMLElement) {
  element.querySelectorAll<HTMLTableElement>('table').forEach((table) => {
    const rows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tr'));
    const tableColumnCount = getTableColumnCount(rows);
    const titleRow = rows[0] && isTableTitleRow(rows[0], tableColumnCount) ? rows[0] : null;

    if (!titleRow) {
      return;
    }

    const columnHeaderRow = rows.slice(1).find(isTableColumnHeaderRow);

    if (!columnHeaderRow) {
      return;
    }

    table.setAttribute(GROUPED_TABLE_ATTRIBUTE, 'true');
    titleRow.cells[0]?.setAttribute(GROUPED_TABLE_TITLE_CELL_ATTRIBUTE, 'true');
    columnHeaderRow.setAttribute(GROUPED_TABLE_HEADER_ROW_ATTRIBUTE, 'true');
  });
}

function isLayoutTable(table: Element | null | undefined) {
  return table instanceof HTMLTableElement && !table.querySelector('th');
}

function applyOutlookFontAttributes(font: HTMLElement, color: string, fontWeight?: string) {
  font.setAttribute('face', OUTLOOK_COPY_FONT_FACE);
  font.setAttribute('size', OUTLOOK_COPY_LEGACY_FONT_SIZE);
  font.setAttribute('color', color);
  font.style.fontFamily = OUTLOOK_COPY_FONT_FAMILY;
  font.style.fontSize = OUTLOOK_COPY_FONT_SIZE;
  font.style.lineHeight = OUTLOOK_COPY_LINE_HEIGHT;
  font.style.color = color;

  if (fontWeight) {
    font.style.fontWeight = fontWeight;
  }
}

function applyOutlookFontColorFallback(cell: HTMLElement, color: string, fontWeight: string) {
  cell.querySelectorAll<HTMLElement>('font').forEach((font) => {
    applyOutlookFontAttributes(font, color, fontWeight);
  });

  const textNodes: Text[] = [];

  function collectTextNodes(node: Node) {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        if (child.textContent?.trim()) {
          textNodes.push(child as Text);
        }
        return;
      }

      if (child instanceof HTMLElement && child.tagName.toLowerCase() !== 'font') {
        collectTextNodes(child);
      }
    });
  }

  collectTextNodes(cell);

  textNodes.forEach((textNode) => {
    const font = cell.ownerDocument.createElement('font');
    applyOutlookFontAttributes(font, color, fontWeight);
    font.textContent = textNode.textContent;
    textNode.replaceWith(font);
  });
}

function applyCellTextStyle(cell: HTMLElement, color: string, fontWeight: string) {
  cell.style.color = color;
  cell.style.fontWeight = fontWeight;

  cell.querySelectorAll<HTMLElement>(OUTLOOK_COPY_TEXT_SELECTOR).forEach((node) => {
    if (node.tagName.toLowerCase() === 'font') {
      node.setAttribute('color', color);
    }
    node.style.color = color;
    node.style.fontWeight = fontWeight;
  });

  applyOutlookFontColorFallback(cell, color, fontWeight);
}

function getSelectedRanges(selection: Selection): Range[] {
  return Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index).cloneRange());
}

function restoreSelection(selection: Selection, ranges: Range[]) {
  selection.removeAllRanges();
  ranges.forEach((range) => selection.addRange(range));
}

function applyOutlookCopyTypography(element: HTMLElement) {
  element.style.fontFamily = OUTLOOK_COPY_FONT_FAMILY;
  element.style.fontSize = OUTLOOK_COPY_FONT_SIZE;
  element.style.lineHeight = OUTLOOK_COPY_LINE_HEIGHT;
  element.style.color = OUTLOOK_COPY_TEXT_COLOR;

  element.querySelectorAll<HTMLElement>(OUTLOOK_COPY_TEXT_SELECTOR).forEach((node) => {
    node.style.fontFamily = OUTLOOK_COPY_FONT_FAMILY;
    node.style.fontSize = OUTLOOK_COPY_FONT_SIZE;
    node.style.lineHeight = OUTLOOK_COPY_LINE_HEIGHT;
  });

  element.querySelectorAll<HTMLElement>('p, li, td, th').forEach((node) => {
    node.style.color = OUTLOOK_COPY_TEXT_COLOR;
  });

  element.querySelectorAll<HTMLElement>('a').forEach((link) => {
    link.style.color = OUTLOOK_COPY_LINK_COLOR;
    link.style.textDecoration = 'underline';
  });
}

function applyCopySpacing(element: HTMLElement) {
  element.querySelectorAll<HTMLElement>('p').forEach((paragraph) => {
    if (paragraph.closest('table')) {
      paragraph.style.margin = '0';
      return;
    }

    paragraph.style.margin = '0 0 12px 0';
  });

  element.querySelectorAll<HTMLElement>('ul, ol').forEach((list) => {
    if (list.closest('table')) {
      list.style.margin = '0';
      list.style.padding = '0';
      return;
    }

    list.style.margin = '0 0 12px 24px';
    list.style.padding = '0';
  });

  element.querySelectorAll<HTMLElement>('li').forEach((listItem) => {
    if (listItem.closest('table')) {
      listItem.style.margin = '0';
      return;
    }

    listItem.style.margin = '0 0 4px 0';
  });

  element.querySelectorAll<HTMLElement>('table').forEach((table) => {
    table.style.margin = '0 0 12px 0';
    table.style.width = '100%';
    table.setAttribute('width', '100%');
    table.setAttribute('cellspacing', '0');
    table.setAttribute('cellpadding', '0');
    table.style.textAlign = 'left';
    table.style.borderStyle = 'none';
    table.style.borderWidth = '0';
    table.style.borderCollapse = 'collapse';
    table.style.borderSpacing = '0';

    if (isLayoutTable(table)) {
      table.setAttribute('border', '0');
      table.style.border = `1px solid ${OUTLOOK_COPY_LAYOUT_TABLE_BORDER_COLOR}`;
    }
  });

  element.querySelectorAll<HTMLElement>('th, td').forEach((cell) => {
    const cellTable = cell.closest('table');
    const borderColor = isLayoutTable(cellTable)
      ? OUTLOOK_COPY_LAYOUT_TABLE_BORDER_COLOR
      : OUTLOOK_COPY_TABLE_BORDER_COLOR;
    cell.style.padding = OUTLOOK_COPY_TABLE_CELL_PADDING;
    cell.style.verticalAlign = 'top';
    cell.style.border = `1px solid ${borderColor}`;
    cell.style.textAlign = 'left';
    cell.setAttribute('align', 'left');
    cell.setAttribute('valign', 'top');
  });

  element.querySelectorAll<HTMLElement>('th').forEach((headerCell) => {
    headerCell.style.backgroundColor = OUTLOOK_COPY_TABLE_HEADER_BACKGROUND_COLOR;
    headerCell.setAttribute('bgcolor', OUTLOOK_COPY_TABLE_HEADER_BACKGROUND_COLOR);
    applyCellTextStyle(headerCell, '#FFFFFF', '700');
  });

  element.querySelectorAll<HTMLTableElement>(`table[${GROUPED_TABLE_ATTRIBUTE}="true"]`).forEach((table) => {
    table.style.width = '100%';

    table.querySelectorAll<HTMLElement>('th, td').forEach((cell) => {
      cell.style.border = `1px solid ${OUTLOOK_COPY_TABLE_BORDER_COLOR}`;
    });

    table.querySelectorAll<HTMLElement>(`[${GROUPED_TABLE_TITLE_CELL_ATTRIBUTE}="true"]`).forEach((titleCell) => {
      titleCell.style.backgroundColor = OUTLOOK_COPY_GROUPED_TABLE_TITLE_BACKGROUND_COLOR;
      titleCell.setAttribute('bgcolor', OUTLOOK_COPY_GROUPED_TABLE_TITLE_BACKGROUND_COLOR);
      applyCellTextStyle(titleCell, OUTLOOK_COPY_GROUPED_TABLE_TITLE_TEXT_COLOR, '700');
    });

    table
      .querySelectorAll<HTMLElement>(`tr[${GROUPED_TABLE_HEADER_ROW_ATTRIBUTE}="true"] > th`)
      .forEach((headerCell) => {
        headerCell.style.backgroundColor = OUTLOOK_COPY_GROUPED_TABLE_HEADER_BACKGROUND_COLOR;
        headerCell.setAttribute('bgcolor', OUTLOOK_COPY_GROUPED_TABLE_HEADER_BACKGROUND_COLOR);
        applyCellTextStyle(headerCell, OUTLOOK_COPY_TEXT_COLOR, '700');
      });
  });
}

function getOutlookFontFallbackColor(parent: HTMLElement) {
  if (parent.closest('a')) {
    return OUTLOOK_COPY_LINK_COLOR;
  }

  const closestTableHeader = parent.closest('th');
  if (!closestTableHeader) {
    return OUTLOOK_COPY_TEXT_COLOR;
  }

  if (closestTableHeader.parentElement?.getAttribute(GROUPED_TABLE_HEADER_ROW_ATTRIBUTE) === 'true') {
    return OUTLOOK_COPY_TEXT_COLOR;
  }

  return OUTLOOK_COPY_GROUPED_TABLE_TITLE_TEXT_COLOR;
}

function getOutlookFontFallbackWeight(parent: HTMLElement) {
  if (parent.closest('th, strong, b')) {
    return '700';
  }

  return undefined;
}

function getOutlookFontFallbackWrapper(parent: HTMLElement) {
  return parent.closest('strong, b') ? 'b' : undefined;
}

function applyOutlookLegacyTextFallbacks(element: HTMLElement) {
  element.querySelectorAll<HTMLElement>('font').forEach((font) => {
    applyOutlookFontAttributes(
      font,
      font.getAttribute('color') ?? getOutlookFontFallbackColor(font),
      getOutlookFontFallbackWeight(font),
    );
  });

  const textNodes: Text[] = [];

  function collectTextNodes(node: Node) {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        if (child.textContent?.trim() && child.parentElement?.tagName.toLowerCase() !== 'font') {
          textNodes.push(child as Text);
        }
        return;
      }

      if (child instanceof HTMLElement && child.tagName.toLowerCase() !== 'font') {
        collectTextNodes(child);
      }
    });
  }

  collectTextNodes(element);

  textNodes.forEach((textNode) => {
    const parent = textNode.parentElement;
    if (!parent) {
      return;
    }

    const font = element.ownerDocument.createElement('font');
    applyOutlookFontAttributes(font, getOutlookFontFallbackColor(parent), getOutlookFontFallbackWeight(parent));
    font.textContent = textNode.textContent;
    const wrapperTagName = getOutlookFontFallbackWrapper(parent);
    if (!wrapperTagName) {
      textNode.replaceWith(font);
      return;
    }

    const wrapper = element.ownerDocument.createElement(wrapperTagName);
    wrapper.style.fontWeight = '700';
    wrapper.append(font);
    textNode.replaceWith(wrapper);
  });
}

function removeCopyExcludedContent(element: HTMLElement) {
  element.querySelectorAll('[data-nova-copy-exclude="true"], button, [role="button"]').forEach((node) => node.remove());
}

function stripPresentationClasses(element: HTMLElement) {
  element.removeAttribute('class');
  element.querySelectorAll<HTMLElement>('[class]').forEach((node) => node.removeAttribute('class'));
}

function unwrapSingleTableContainers(element: HTMLElement) {
  let didUnwrap = true;

  while (didUnwrap) {
    didUnwrap = false;
    element.querySelectorAll<HTMLElement>('div').forEach((container) => {
      const [onlyChild] = Array.from(container.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement,
      );

      if (onlyChild && container.children.length === 1 && onlyChild.tagName.toLowerCase() === 'table') {
        container.replaceWith(onlyChild);
        didUnwrap = true;
      }
    });
  }
}

function prepareCopyContent(element: HTMLElement) {
  removeCopyExcludedContent(element);
  unwrapSingleTableContainers(element);
  stripPresentationClasses(element);
  prepareGroupedHeaderTables(element);
  applyOutlookCopyTypography(element);
  applyCopySpacing(element);
  applyOutlookLegacyTextFallbacks(element);
}

function cloneElementForCopy(element: HTMLElement): HTMLElement {
  const copyElement = element.cloneNode(true) as HTMLElement;
  prepareCopyContent(copyElement);

  return copyElement;
}

function createCopyElement(element: HTMLElement): HTMLElement {
  const copyElement = cloneElementForCopy(element);
  copyElement.style.position = 'fixed';
  copyElement.style.left = '-9999px';
  copyElement.style.top = '0';
  copyElement.style.width = `${Math.max(element.offsetWidth, 1)}px`;
  copyElement.style.pointerEvents = 'none';
  copyElement.setAttribute('aria-hidden', 'true');
  document.body.append(copyElement);

  return copyElement;
}

function getCopyPlainText(element: HTMLElement) {
  return element.innerText || element.textContent || '';
}

async function writeHtmlToClipboard(html: string, plainText: string): Promise<boolean> {
  const clipboardItem = globalThis.ClipboardItem as ClipboardItemConstructor | undefined;

  if (typeof navigator === 'undefined' || !navigator.clipboard?.write || !clipboardItem) {
    return false;
  }

  try {
    await navigator.clipboard.write([
      new clipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
      }),
    ]);
    return true;
  } catch {
    return false;
  }
}

function isRangeWithinElement(range: Range, element: HTMLElement) {
  return element.contains(range.commonAncestorContainer);
}

function cloneSelectedContent(ranges: Range[], document: Document) {
  const copyElement = document.createElement('div');

  ranges.forEach((range) => {
    copyElement.append(range.cloneContents());
  });

  prepareCopyContent(copyElement);

  return copyElement;
}

export async function copyElementToClipboard(element: HTMLElement | null): Promise<boolean> {
  if (!element?.textContent?.trim()) {
    return false;
  }

  const directCopyElement = cloneElementForCopy(element);
  if (directCopyElement.textContent?.trim()) {
    const copiedDirectly = await writeHtmlToClipboard(directCopyElement.outerHTML, getCopyPlainText(directCopyElement));
    if (copiedDirectly) {
      return true;
    }
  }

  const selection = window.getSelection();
  if (!selection) {
    return false;
  }

  if (typeof document.execCommand !== 'function') {
    return false;
  }

  const previousRanges = getSelectedRanges(selection);
  const copyElement = createCopyElement(element);
  const range = document.createRange();
  range.selectNodeContents(copyElement);

  try {
    selection.removeAllRanges();
    selection.addRange(range);
    return document.execCommand('copy');
  } finally {
    restoreSelection(selection, previousRanges);
    copyElement.remove();
  }
}

export function copySelectedContentToClipboard(event: ClipboardCopyEvent, element: HTMLElement | null): boolean {
  if (!element || !event.clipboardData) {
    return false;
  }

  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || !selection.toString().trim()) {
    return false;
  }

  const selectedRanges = getSelectedRanges(selection);
  if (!selectedRanges.length || !selectedRanges.every((range) => isRangeWithinElement(range, element))) {
    return false;
  }

  const copyElement = cloneSelectedContent(selectedRanges, element.ownerDocument);
  if (!copyElement.textContent?.trim()) {
    return false;
  }

  event.clipboardData.setData('text/html', copyElement.outerHTML);
  event.clipboardData.setData('text/plain', selectedRanges.map((range) => range.toString()).join('\n'));
  event.preventDefault();

  return true;
}

function copyTextWithExecCommand(text: string): boolean {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '0';
  textarea.style.opacity = '0';
  document.body.append(textarea);

  const selection = typeof window !== 'undefined' ? window.getSelection() : null;
  const previousRanges = selection ? getSelectedRanges(selection) : [];

  try {
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);
    return document.execCommand('copy');
  } finally {
    if (selection) {
      restoreSelection(selection, previousRanges);
    }
    textarea.remove();
  }
}

export async function writeTextToClipboard(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall back to the selection-based copy path below for browser contexts
      // where the Clipboard API exists but rejects.
    }
  }

  if (copyTextWithExecCommand(text)) {
    return;
  }

  throw new Error('Unable to write text to clipboard');
}
